import {
  ChangeEventHandler,
  DragEventHandler,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import { convertBytesToMb, validateAllFilesType, getFileNameFromUrl } from 'utils';

import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg';
import { ReactComponent as ArtworkIcon } from 'assets/images/artwork.svg';

import styles from './styles.module.scss';

type Props = {
  title: string;
  label: string;
  hint: string;
  accept: string;
  selectedFiles: (string | File)[] | null;
  icon: React.FC;
  multiple?: boolean;
  error?: string;
  onChange: (files: File[]) => void;
  onSelectedFileDelete: (file: File | string) => void;
};

export const Uploader: React.FC<Props> = memo(function Uploader({
  title,
  label,
  hint,
  accept,
  selectedFiles,
  icon: Icon,
  error,
  multiple = false,
  onChange,
  onSelectedFileDelete,
}) {
  const [draggingFile, setDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBrowseFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const selectedFiles = Array.from(event.target.files!);

      if (validateAllFilesType(selectedFiles, accept)) {
        onChange(selectedFiles);
      }
    },
    [accept, onChange],
  );

  const handleFilesDragEnter: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingFile(true);
  }, []);

  const handleFilesDragOver: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleFilesDragLeave: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingFile(false);
  }, []);

  const handleFilesDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const draggedFiles = Array.from(event.dataTransfer.files);

      if (validateAllFilesType(draggedFiles, accept)) {
        onChange(draggedFiles);
      }

      setDraggingFile(false);
    },
    [onChange, accept],
  );

  const handleSelectedFileDelete = useMemo(() => {
    return (selectedFile: File | string) => {
      return () => {
        onSelectedFileDelete(selectedFile);
      };
    };
  }, [onSelectedFileDelete]);

  return (
    <div className={styles.container}>
      <h6>{title}</h6>
      {selectedFiles && selectedFiles.length > 0 ? (
        <div className={classNames(styles.selected_file_container, { [styles.error_box]: error })}>
          {selectedFiles.map((selectedFile) => (
            <div
              key={
                selectedFile instanceof File
                  ? `${selectedFile.name} ${selectedFile.size}`
                  : selectedFile
              }
              className={styles.selected_file}>
              <div>
                <div>
                  <ArtworkIcon />
                </div>
                <div>
                  <p>
                    {selectedFile instanceof File
                      ? selectedFile.name
                      : getFileNameFromUrl(selectedFile)}
                  </p>
                  {selectedFile instanceof File && <p>{convertBytesToMb(selectedFile.size)} mb</p>}
                </div>
              </div>
              <DeleteIcon onClick={handleSelectedFileDelete(selectedFile)} />
            </div>
          ))}
        </div>
      ) : (
        <div
          className={classNames(styles.upload_box, {
            [styles.upload_box_dragging]: draggingFile,
            [styles.error_box]: error,
          })}
          onDragEnter={handleFilesDragEnter}
          onDragOver={handleFilesDragOver}
          onDragLeave={handleFilesDragLeave}
          onDrop={handleFilesDrop}>
          <Icon />
          <p>
            {label} or <span onClick={handleBrowseFiles}>browse</span> files
          </p>
          <input
            type="file"
            ref={fileInputRef}
            accept={accept}
            multiple={multiple}
            onChange={handleFileUpload}
          />
        </div>
      )}
      {error && <span>{error}</span>}
      <p>{hint}</p>
    </div>
  );
});
